import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ProductVariant } from '@/hooks/useProductVariants';

export type ProductCategory = string;
export type UpdateFrequency = 'yearly' | 'quarterly' | 'monthly' | 'weekly';

export interface FileColumn {
  name: string;
  description: string;
  start?: number;
  length?: number;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  short_description: string;
  full_description: string | null;
  ws_category: string;
  price: number;
  currency: string;
  sku: string | null;
  available_countries: string[];
  file_format: string[];
  encoding: string[] | null;
  update_frequency: UpdateFrequency | null;
  row_count: string | null;
  coordinate_systems: string[] | null;
  coverage: string | null;
  category: string | null;
  spec_type: 'encoding' | 'coordinate_system' | 'both';
  is_active: boolean;
  sort_order: number;
  file_columns: FileColumn[] | null;
  translations: Record<string, { name?: string; short_description?: string; full_description?: string }> | null;
  created_at: string;
  updated_at: string;
  variants?: ProductVariant[];
}

async function fetchAllProducts(): Promise<Product[]> {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const res = await fetch(
    `https://${projectId}.supabase.co/functions/v1/products-api`,
    { headers: { apikey: anonKey } }
  );
  if (!res.ok) throw new Error(`Failed to fetch products: ${res.status}`);
  return await res.json();
}

async function fetchProductBySlug(slug: string): Promise<Product | null> {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const res = await fetch(
    `https://${projectId}.supabase.co/functions/v1/products-api?slug=${encodeURIComponent(slug)}`,
    { headers: { apikey: anonKey } }
  );
  if (!res.ok) throw new Error(`Failed to fetch product: ${res.status}`);
  return await res.json();
}

export function useProducts(category?: string) {
  return useQuery({
    queryKey: ['products', category],
    queryFn: async () => {
      const products = await fetchAllProducts();
      const active = products.filter((p) => p.is_active);
      if (category) {
        return active.filter((p) => p.ws_category === category);
      }
      return active;
    },
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: () => fetchProductBySlug(slug),
    enabled: !!slug,
  });
}
